/**
 * Daily.co Video Call Component
 * Displays video participants and provides call controls
 */
import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Video, VideoOff, Mic, MicOff, Monitor, PhoneOff, Loader2 } from 'lucide-react';

/**
 * Individual participant video tile
 */
export const ParticipantTile = ({ callObject, participantId, isLocal = false }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [participant, setParticipant] = React.useState(null);

  useEffect(() => {
    if (!callObject || !participantId) return;

    const updateParticipant = () => {
      const participants = callObject.participants();
      const p = isLocal ? participants.local : participants[participantId];
      setParticipant(p);
    };

    // Initial update
    updateParticipant();

    // Listen for updates
    callObject.on('participant-updated', updateParticipant);
    callObject.on('participant-joined', updateParticipant);

    return () => {
      callObject.off('participant-updated', updateParticipant);
      callObject.off('participant-joined', updateParticipant);
    };
  }, [callObject, participantId, isLocal]);

  useEffect(() => {
    if (!participant) return;

    // Attach video track
    if (participant.videoTrack && videoRef.current) {
      videoRef.current.srcObject = new MediaStream([participant.videoTrack]);
    }

    // Attach audio track (mute for local participant)
    if (participant.audioTrack && audioRef.current && !isLocal) {
      audioRef.current.srcObject = new MediaStream([participant.audioTrack]);
    }
  }, [participant, isLocal]);

  if (!participant) {
    return (
      <Card className="aspect-video bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </Card>
    );
  }

  const isVideoOn = participant.video;
  const isAudioOn = participant.audio;
  const userName = participant.user_name || 'Unknown';

  return (
    <Card className="relative aspect-video bg-slate-900 overflow-hidden group">
      {isVideoOn ? (
        <video
          ref={videoRef}
          autoPlay
          muted={isLocal}
          playsInline
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
          <Avatar className="w-20 h-20">
            <AvatarFallback className="text-2xl bg-blue-600 text-white">
              {userName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>
      )}

      <audio ref={audioRef} autoPlay />

      {/* Participant info overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
        <div className="flex items-center justify-between">
          <span className="text-white font-medium text-sm truncate">
            {userName} {isLocal && '(You)'}
          </span>
          <div className="flex items-center gap-2">
            {!isAudioOn && <MicOff className="w-4 h-4 text-red-400" />}
            {!isVideoOn && <VideoOff className="w-4 h-4 text-red-400" />}
          </div>
        </div>
      </div>

      {/* Screen share indicator */}
      {participant.screen && (
        <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
          <Monitor className="w-3 h-3" />
          Sharing
        </div>
      )}
    </Card>
  );
};

/**
 * Video call controls
 */
export const CallControls = ({
  onToggleVideo,
  onToggleAudio,
  onToggleScreenShare,
  onEndCall,
  isVideoOn,
  isAudioOn,
  isScreenSharing,
  canScreenShare = true,
  disabled = false
}) => {
  return (
    <div className="flex items-center justify-center gap-3 bg-slate-900 p-4 rounded-lg">
      <Button
        onClick={onToggleAudio}
        variant={isAudioOn ? 'default' : 'destructive'}
        size="lg"
        className="rounded-full w-12 h-12 p-0"
        disabled={disabled}
        data-testid="toggle-audio-btn"
      >
        {isAudioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
      </Button>

      <Button
        onClick={onToggleVideo}
        variant={isVideoOn ? 'default' : 'destructive'}
        size="lg"
        className="rounded-full w-12 h-12 p-0"
        disabled={disabled}
        data-testid="toggle-video-btn"
      >
        {isVideoOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
      </Button>

      {canScreenShare && (
        <Button
          onClick={onToggleScreenShare}
          variant={isScreenSharing ? 'default' : 'outline'}
          size="lg"
          className="rounded-full w-12 h-12 p-0"
          disabled={disabled}
          data-testid="toggle-screenshare-btn"
        >
          <Monitor className="w-5 h-5" />
        </Button>
      )}

      <Button
        onClick={onEndCall}
        variant="destructive"
        size="lg"
        className="rounded-full w-12 h-12 p-0"
        disabled={disabled}
        data-testid="end-call-btn"
      >
        <PhoneOff className="w-5 h-5" />
      </Button>
    </div>
  );
};

/**
 * Main video call component
 */
export const VideoCallGrid = ({ callObject, localParticipant, onControlAction }) => {
  const participantIds = useParticipantIds(callObject);

  if (!callObject) {
    return (
      <Card className="aspect-video bg-slate-900 flex items-center justify-center">
        <div className="text-center text-slate-400">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p>Initializing video call...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid gap-4" style={{
      gridTemplateColumns: participantIds.length > 1 ? 'repeat(2, 1fr)' : '1fr'
    }}>
      {/* Local participant */}
      {localParticipant && (
        <ParticipantTile
          participantId={localParticipant.session_id}
          isLocal={true}
        />
      )}

      {/* Remote participants */}
      {participantIds
        .filter(id => id !== localParticipant?.session_id)
        .map(id => (
          <ParticipantTile key={id} participantId={id} isLocal={false} />
        ))}
    </div>
  );
};
